import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Spin,
  DatePicker,
  Select,
  Button,
  Table,
  Switch
} from 'antd';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  notificationsAtom,
} from '../../../../../atoms/Atoms';
import api from '../../../../../api/api';
import Card from '../../../../shared-components/container/Card';
import { getDatePresets, thousand } from '../../../../../functions';
import { mapContainerStyle } from '../../../../../utils';
import envConfig from '../../../../../envConfig';

function InstallMaps({ orgCode, useGlobal }) {
  const referralCode = orgCode;
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [useDateFilters, setUseDateFilters] = useState(false);
  const [mapState, setMapState] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const defaultZoom = 11;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: envConfig.GoogleApiKey,
    // language: `${i18n.language}-US`,
    language: 'en-US',
    libraries: ['places']
  });

  const apiHasLoaded = (map) => {
    const bounds = new window.google.maps.LatLngBounds({
      lat: 18.2491543,
      lng: -66.4895876,
    });
    map.fitBounds(bounds);
    map.setZoom(defaultZoom);
    setMapState(map);
  };

  function getData() {
    setLoading(true);
    let url = 'reports/myinstalls';
    if (referralCode) {
      url += `?orgCode=${referralCode}`;
    }
    if (useDateFilters) {
      let concatSymbol = '?';
      if (url.includes('orgCode')) {
        concatSymbol = '&';
      }
      const startDate = dayjs(dateRange.start);
      const endDate = dayjs(dateRange.end);
      const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
      const endString = endDate.add(4, 'hour').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
      url += `${concatSymbol}startDate=${startString}&endDate=${endString}`;
    }
    api.get(url)
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setData(response.data.data);
        } else {
          setData(null);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            }
          ]);
      });
  }

  useEffect(() => {
    setData(null);
    getData();
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    if (useDateFilters) {
      getData();
    }
  }, [dateRange]);

  return (
    <div>
      <div className="grid gap-4 my-4 grid-cols-1 md:grid-cols-2">
        <Spin spinning={loading}>
          <button
            aria-label={t('installed')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 0 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(0)}
          >
            <Card
              title={t('installed')}
              description={thousand(data?.installed?.length)}             
            />
          </button>
        </Spin>
        <Spin spinning={loading}>
          <button
            aria-label={t('scheduled_installs')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(1)}
          >
            <Card
              title={t('scheduled_installs')}
              description={thousand(data?.scheduledInstalls?.length)}             
            />
          </button>
        </Spin>
      </div>
      <Spin spinning={loading}>
        <div className="space-y-2">
          <div className="flex">
            <div className="ml-auto" />
            {useDateFilters && (
              <RangePicker
                className="bg-transparent"
                presets={getDatePresets(t)}
                disabled={loading}
                value={[
                  dayjs(dateRange?.start),
                  dayjs(dateRange?.end)
                ]}
                onChange={(obj) => {
                  setDateRange({
                    start: obj[0],
                    end: obj[1]
                  });
                }}
                format="MMM DD, YYYY"
              />
            )}
            <span className="mt-1 ml-2 mr-1">{t('use_date_filters')}</span>
            <Switch
              className="mt-1 mr-2"
              checked={useDateFilters}
              onChange={(obj) => setUseDateFilters(obj)}
            />
            <Button
              onClick={() => {
                mapState.setZoom(defaultZoom);
                getData();
              }}
              className="mt-1 mb-2"
              disabled={loading}
              type="primary"
              size="small"
            >
              {t('reload')}
            </Button>
          </div>
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: '500px'
            }}
            zoomControl
            draggable
            mapTypeId="satellite"
            center={{
              lat: 18.2491543,
              lng: -66.4895876,
            }}
            zoom={defaultZoom}
            onLoad={apiHasLoaded}
          >
            {_.map(selectedTab === 0 ? data?.installed : data?.scheduledInstalls, (i) => (
              <Marker
                key={`${i.lattitude} ${i.longitude}`}
                position={{ lat: i.lattitude, lng: i?.longitude }}
              />
            ))}
          </GoogleMap>
          <Table
            bordered
            size="small"
            dataSource={selectedTab === 0 ? data?.installed : data?.scheduledInstalls}
            columns={[
              {
                title: t('closer'),
                align: 'left',
                className: 'text-sm',
                render: (row) => (
                  <p>{row.closer?.completeName}</p>
                ),
              },
              {
                title: t('deal_id'),
                align: 'left',
                className: 'text-sm',
                render: (row) => (
                  <p>{row.externalDealId}</p>
                ),
              },
              {
                title: `${t('latitude')}/${t('longitude')}`,
                align: 'left',
                className: 'text-sm',
                render: (row) => (
                  <p>{row.lattitude}, {row.longitude}</p>
                ),
              }
            ]}
          />
        </div>
      </Spin>
    </div>
  );
}

export default InstallMaps;
